<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useQueueStore } from '@state/stores/queue'
import { usePlaybackStore } from '@state/stores/playback'
import waveform from '@components/waveform'
import modalService from '@/services/modal-service'
import { usePlayerService } from '@composables/use-player-service'
import { useQueueService } from '@composables/use-queue-service'
import { useAuthStore } from '@state/stores/auth'
import { useChannelsStore } from '@state/stores/channels'
import { useSettingsStore } from '@state/stores/settings'
import formatMs from '@utils/format-ms'

let volumeDisplayTime = null

const authStore = useAuthStore()
const queueStore = useQueueStore()
const playbackStore = usePlaybackStore()
const channelsStore = useChannelsStore()
const settingsStore = useSettingsStore()
const playerService = usePlayerService()
const queueService = useQueueService()
const isVolumeActive = ref(false)
const playback = computed(() => playbackStore)
const resource = computed(() => playback.value.resource)
const isChannelPlaying = computed(() => resource.value?.type === 'channel')
const isMixPlaying = computed(() => resource.value && !isChannelPlaying.value)
const queueLength = computed(() => queueStore?.mixes?.length || null)
const queuePosition = computed(() => queueStore?.position)
const isQueuePlaying = computed(() => Boolean(queueLength.value))
const isFirstPosition = computed(() => isQueuePlaying.value && queuePosition.value === 0)
const isLastPosition = computed(() =>
    isQueuePlaying.value && queuePosition.value === queueLength.value - 1
)
const timeFraction = computed(() =>
    isMixPlaying.value && playback.value.currentTime
        ? playback.value.currentTime / playback.value.duration
        : false
)
const duration = computed(() =>
    isMixPlaying.value && playback.value.duration > 0
        ? formatMs(playback.value.duration)
        : false
)
const currentTime = computed(() =>
    isMixPlaying.value && playback.value.currentTime >= 0
        ? formatMs(playback.value.currentTime)
        : false
)
const channelsNotPlaying = computed(() =>
    isChannelPlaying.value
        ? channelsStore.channels.filter(channel => channel.id !== resource.value.id)
        : channelsStore.channels
)
const mix = computed(() => playbackStore.mix)
const status = computed(() => playbackStore.status)
const volume = computed({
    get: () => settingsStore.volume,
    set(value) {
        playerService.setVolume(value > 95 && settingsStore.volume > 95 ? 100 : value)
    },
})
const volumeIcon = computed(() => {
    if (volume.value > 75) return 'volume_up'
    if (volume.value > 25) return 'volume_down'
    if (volume.value > 0) return 'volume_mute'
    return 'volume_off'
})
const loggedIn = computed(() => authStore.loggedIn)

watch(volume, () => {
    clearTimeout(volumeDisplayTime)
    isVolumeActive.value = true
    volumeDisplayTime = setTimeout(() => {
        isVolumeActive.value = false
    }, 1000)
})

function playRadioChannel(channel) {
    if (!loggedIn.value) {
        modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
        return
    }
    queueService.playRadioChannel(channel.id)
}

function mute() {
    playerService.toggleMute()
}

function setVolume({ offsetY }) {
    volume.value = 100 - offsetY
}

onBeforeUnmount(() => clearTimeout(volumeDisplayTime))
</script>

<template>
    <div class="music-player">
        <div class="main">
            <div class="selector">
                <div :class="['channels', 'selector-dropdown']">
                    <div
                        v-if="isChannelPlaying"
                        :class="['channels-item', 'channels-item-active']"
                        @click="queueService.pause()"
                    >
                        <BaseIcon
                            class="channels-icon"
                            :name="
                                playback.status === 'playing' || playback.status === 'loading' ? 'pause' : 'play_arrow'
                            "
                        />
                        {{ resource.title }}
                    </div>
                    <div
                        v-for="channel in channelsNotPlaying"
                        :key="channel.id"
                        class="channels-item"
                        @click="playRadioChannel(channel)"
                    >
                        <BaseIcon
                            class="channels-icon"
                            name="play_arrow"
                        /> {{ channel.title }}
                    </div>
                </div>
                <div class="selector-trigger">
                    <div
                        v-if="isChannelPlaying"
                        :class="['selector-channel']"
                    >
                        {{ resource.title }}
                    </div>
                    <div class="selector-title">
                        {{
                            playback.resourceType === 'mix'
                                ? 'Radio Channel'
                                : playback.resourceType === 'playlist'
                                    ? 'Playlist'
                                    : 'Radio Channel'
                        }}
                    </div>
                    <BaseIcon
                        :class="['selector-icon', 'selector-icon-less']"
                        name="expand_less"
                    />
                    <BaseIcon
                        :class="['selector-icon', 'selector-icon-more']"
                        name="expand_more"
                    />
                </div>
            </div>

            <div class="meta">
                <div
                    v-if="mix && mix.show && mix.episode"
                    class="meta-inner"
                >
                    <BaseLink
                        class="image-container"
                        :to="{ name: 'mix', params: { id: mix.id } }"
                    >
                        <BaseResourceImage
                            :resource="mix.episode"
                            class="image"
                        />
                    </BaseLink>
                    <BaseFavourites
                        v-if="mix"
                        class="favorites"
                        :mix="mix"
                        size="icon-only"
                        button-type="transparent"
                    />
                    <div>
                        <div :class="['title', 'title']">
                            <BaseLink :to="{ path: `/artist/${mix.artist.url}/${mix.artist.id}` }">
                                {{ mix.artist.title }}
                            </BaseLink>
                        </div>
                        <div :class="['artist', 'artist']">
                            <BaseLink :to="{ name: 'show', params: { id: mix.show.id } }">
                                {{ mix.show.title }}
                            </BaseLink>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="isMixPlaying"
                class="center-wrap"
            >
                <div :class="['time', 'time-elapsed']">{{ currentTime || '' }}</div>
                <div class="waveform-wrap">
                    <waveform
                        :key="resource.id"
                        :time-fraction="timeFraction"
                        :duration="playback.duration"
                    />
                </div>
                <div class="time">{{ duration || '' }}</div>
            </div>

            <div :class="['controls', 'controls-secondary']">
                <div class="volume-wrap">
                    <div
                        class="icon-wrap"
                        @click="mute"
                    >
                        <BaseIcon
                            class="player-icon icon-volume"
                            :name="volumeIcon"
                            preserve-aspect-ratio="xMinYMid"
                        />
                    </div>
                    <div :class="['volume-slider', isVolumeActive ? 'active' : '']">
                        <div
                            class="volume-slider-inner"
                            @click.prevent.stop="setVolume"
                        >
                            <div
                                class="volume-slider-value"
                                :style="{ height: `${volume}%` }"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div :class="['controls', 'controls-main']">
                <div @click="queueService.playPrevious()">
                    <BaseIcon
                        :class="['player-icon', 'icon-skip', { active: isQueuePlaying && !isFirstPosition }]"
                        name="skip_previous"
                    />
                </div>
                <div
                    :class="['play', !resource && 'play-faded']"
                    @click="queueService.pause()"
                >
                    <BaseIcon
                        v-if="status === 'playing'"
                        class="player-icon"
                        name="pause"
                    />
                    <BaseIcon
                        v-else-if="status === 'loading'"
                        class="player-icon icon-spinning"
                        name="spinner"
                    />
                    <BaseIcon
                        v-else
                        class="player-icon"
                        name="play_arrow"
                    />
                </div>

                <div @click="queueService.playNext()">
                    <BaseIcon
                        :class="['player-icon', 'icon-skip', { active: isQueuePlaying && !isLastPosition }]"
                        name="skip_next"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:math';
@use '@design' as *;

.music-player {
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: $layer-overlay-z-index;
    width: 100%;
}

.main {
    display: flex;
    align-items: space-between;
    justify-content: space-between;
    width: 100%;
    height: $size-music-player;
    background: $c-grey-woodsmoke;
}

.image {
    display: block;
    width: $size-music-player;
    height: $size-music-player;

    &-container {
        display: none;

        @include desktop {
            display: block;
        }
    }
}

.selector {
    display: none;

    @include desktop {
        display: block;
    }

    &-trigger {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 200px;
        height: 100%;
        padding: 0 $s-m;
    }

    &-dropdown {
        position: absolute;
        top: 0;
        left: 0;
        z-index: $layer-very-negative-z-index;
        width: 280px;
        padding: $s-s $s-m;
        background-color: $c-black;
        transform: translate3d(0, 0%, 0);
        transition: $t;
    }

    &:hover {
        .selector-dropdown {
            transform: translate3d(0, -100%, 0);
        }
    }

    &-channel {
        text-transform: uppercase;

        @extend %contentTitleS;
    }

    &-title {
        @extend %contentSubtitleS;
    }

    &-icon {
        position: absolute;
        top: 50%;
        right: $s-s;
        display: none;
        margin-top: -0.75rem;
        fill: $c-highlight;

        @include square(1.5rem);
    }

    &-icon-more {
        display: block;
    }

    &:hover &-icon-more {
        display: none;
    }

    &:hover &-icon-less {
        display: block;
    }
}

.channels {
    &-selector-wrap {
        width: 200px;
    }

    &-item {
        position: relative;
        padding: $s-s;
        padding-left: $s-xl + $s-m;
        text-transform: uppercase;
        cursor: pointer;

        @extend %contentTitleS;

        &-active {
            padding-bottom: $s-m;
            margin-bottom: $s-xxs;
            color: $c-highlight;
            border-bottom: 1px solid rgba($c-dusty-gray, 0.1);
        }
    }

    &-icon {
        position: absolute;
        top: 50%;
        left: 0;
        width: $s-xl;
        height: 0.8rem;
        margin-top: -0.4rem;
        fill: $c-white;
    }
}

.meta {
    &-inner {
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    @include desktop {
        min-width: 12rem;
    }
}

.music-player .favorites {
    display: flex;
    align-content: center;
    justify-content: center;
    width: $size-music-player;
    min-width: $size-music-player;
    height: $size-music-player;
    min-height: $size-music-player;
    margin-bottom: 0;
}

.center-wrap {
    display: flex;
    align-items: center;
    margin-right: auto;
    margin-left: auto;
}

.waveform-wrap {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    transform: translateY(-100%);

    @include desktop {
        position: relative;
        top: auto;
        left: auto;
        width: 36vw;
        max-width: 480px;
        height: 1rem;
        transform: none;
    }
}

.time {
    @extend %contentSubtitleS;

    display: none;
    width: 7em;
    padding: 0 $s-s;

    @include desktop {
        display: block;
    }

    &-elapsed {
        color: $c-white;
        text-align: right;
    }
}

.heart-icon {
    @include square(1rem);

    fill: $c-white;

    @include center;

    &-full {
        opacity: 0;
    }
}

.heart {
    position: relative;

    @include square(1.5rem);

    margin-right: 0.5rem;

    &:hover .heart-icon-full {
        opacity: 1;
    }
}

.title {
    font-size: 11px;
    font-weight: 600;
    line-height: 14px;
    color: $c-white;
}

.artist {
    font-size: 11px;
    line-height: 14px;
    color: $c-text-faded;
}

@keyframes spin {
    100% {
        transform: rotate(360deg);
    }
}

.player-icon {
    @include square(1.5rem);

    box-sizing: content-box;
    fill: $c-white;
}

.icon-volume {
    cursor: pointer;
}

.icon-spinning {
    animation: spin 2s linear infinite;
}

.icon-skip {
    padding: 0 math.div($s-s, 2);
    fill: $c-text-faded;

    @include square($s-xl);

    &.active {
        cursor: pointer;
        fill: $c-white;
    }
}

.controls {
    display: flex;
    align-items: center;
    justify-content: center;

    &-main {
        padding: 0 math.div($s-s, 2);
        margin-left: auto;

        @include desktop {
            margin-left: 0;
        }
    }

    &-secondary {
        display: none;
        margin-left: auto;

        @include desktop {
            display: flex;
        }

        .player-icon {
            padding: $s-s;
            fill: $c-grey-abbey;
        }
    }
}

.play {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 math.div($s-s, 2);
    cursor: pointer;
    background: $c-button-bg;
    border-radius: 50%;

    @include square($s-m * 2);

    .icon {
        @include square(22px);

        fill: $c-body-bg;
    }

    &-faded {
        cursor: default;
        background: $c-grey-abbey;
    }
}

.volume {
    padding: 20px 0;

    &-wrap {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        background: $c-grey-woodsmoke;
        transition: $t;

        &:hover {
            background: $c-black;

            .icon {
                fill: $c-white;
            }
        }
    }

    &-slider {
        position: absolute;
        top: 0;
        z-index: $layer-negative-z-index;
        display: flex;
        justify-content: center;
        width: 100%;
        padding-top: $s-s;
        padding-bottom: $s-s + $s-xs;
        overflow: hidden;
        transform: translateY(0%);
        transition: $t;

        &-inner {
            position: relative;
            z-index: $layer-page-z-index;
            box-sizing: content-box;
            width: 100%;
            height: 100px;
            padding: 0 $s-m;
            cursor: pointer;

            &::before {
                position: absolute;
                top: -$s-s;
                left: 0;
                display: block;
                width: 100%;
                height: calc(100% + #{2 * $s-s});
                pointer-events: none;
                content: '';
                background-color: $c-black;
            }

            &::after {
                position: absolute;
                top: 0;
                left: 50%;
                display: block;
                width: 4px;
                height: 100%;
                margin-left: -2px;
                pointer-events: none;
                content: '';
                background-color: $c-mine-shaft;
            }
        }

        &-value {
            position: absolute;
            bottom: 0;
            left: 50%;
            z-index: $layer-page-z-index;
            width: 4px;
            height: 100%;
            margin-left: -2px;
            pointer-events: none;
            background-image: $c-button-bg;
            transform: scaleY(100%);
            transition: $t;
        }
    }

    &-wrap:hover .volume-slider {
        transform: translateY(-100%);
    }
}

.volume-slider.active {
    transform: translateY(-100%);
}

.queue-wrap {
    .icon-queue {
        fill: $c-mine-shaft;
    }

    &.active {
        cursor: pointer;

        .icon-queue {
            fill: $c-grey-abbey;
        }

        &:hover {
            background: $c-black;

            .icon-queue {
                fill: $c-white;
            }
        }
    }
}
</style>
